/**
 * Hook centralizado para carregar dados de ferramentas
 *
 * Encapsula lógica de carregamento, validação e cache para dados de ferramentas.
 * Usa useApiCache internamente com suporte a ETag para cache inteligente.
 *
 * O backend retorna headers ETag + Cache-Control, permitindo:
 * - Cache local com validação via If-None-Match
 * - Resposta 304 Not Modified quando dados não mudaram
 * - Redução significativa de latência e carga no servidor
 */

import { useApiCache } from "../../../lib/useApiCache";
import { validateToolDataWithCustom } from "../utils/toolDataUtils";

export interface UseToolDataOptions {
	/** Slug da ferramenta (ex: 'converter-para-bitmap') */
	slug: string;
	/** Tempo de vida do cache em milissegundos (padrão: 30 minutos - alinhado com backend) */
	ttlMs?: number;
	/**
	 * @deprecated Use useETag: true (padrão) em vez de skipCache.
	 * Se true, sempre busca dados frescos (ignora cache).
	 * Para forçar refresh, use a função refresh() retornada pelo hook.
	 */
	skipCache?: boolean;
	/** Validação customizada adicional (opcional) */
	validate?: (data: any) => boolean;
	/**
	 * Se true (padrão), usa cache baseado em ETag HTTP.
	 * - Envia If-None-Match com ETag do cache
	 * - Se servidor retorna 304, usa dados do cache local (super rápido)
	 * - Se servidor retorna 200, atualiza cache com novos dados
	 */
	useETag?: boolean;
}

export interface UseToolDataResult<T = any> {
	/** Dados da ferramenta */
	data: T | null;
	/** Indica se está carregando */
	loading: boolean;
	/** Mensagem de erro (null se não houver erro) */
	error: string | null;
	/** Código do erro para tratamento específico */
	errorCode: "network" | "validation" | "auth" | "unknown" | null;
	/** Força refresh dos dados (ignora cache) */
	refresh: () => Promise<void>;
	/** Indica se já tentou carregar pelo menos uma vez */
	hasAttempted: boolean;
	/** Indica se os dados vieram do cache (304 Not Modified) - só com useETag */
	fromCache?: boolean;
	/** ETag atual dos dados - só com useETag */
	etag?: string;
}

/**
 * Hook para carregar dados de uma ferramenta da API
 *
 * @example
 * ```tsx
 * // Uso padrão com ETag (recomendado)
 * const { data, loading, error, fromCache } = useToolData({
 *   slug: 'converter-para-bitmap',
 *   validate: (data) => data.defaults?.dpi > 0
 * });
 *
 * // fromCache === true significa que veio do cache (304 Not Modified)
 * // Use refresh() para forçar busca de dados novos
 * ```
 */
export function useToolData<T = any>(
	options: UseToolDataOptions,
): UseToolDataResult<T> {
	const {
		slug,
		ttlMs = 30 * 60 * 1000, // 30 minutos (alinhado com TTL do backend)
		skipCache = false,
		validate,
		useETag = true, // ETag habilitado por padrão
	} = options;

	// Construir endpoint
	const endpoint = `/tools/${slug}`;

	// Chave de cache baseada no slug
	const cacheKey = `tool:${slug}`;

	// Usar useApiCache com ETag ou modo tradicional
	const result = useApiCache<T>(cacheKey, endpoint, {
		ttlMs,
		area: "local", // localStorage para persistir entre sessões
		useETag: useETag && !skipCache, // ETag só se não tiver skipCache
		skipCache: skipCache && !useETag, // Modo legado
		validate: (data) => {
			// Validação base compartilhada
			if (!validateToolDataWithCustom(data, validate)) {
				return false;
			}
			return true;
		},
	});

	return {
		data: result.data,
		loading: result.loading,
		error: result.error,
		errorCode: result.errorCode,
		refresh: result.refresh,
		hasAttempted: result.hasAttempted,
		fromCache: result.fromCache,
		etag: result.etag,
	};
}
